import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Button,
  Avatar,
  Input,
  Chip,
  Spinner
} from '@heroui/react';
import { X, Search, Users, MessageCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const Members = ({ onClose }) => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // const [totalPages, setTotalPages] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const membersPerPage = 20;

  useEffect(() => {
    fetchMembers(true); // Reset when search term changes or initial load
  }, [searchTerm]);

  // Check if content fills viewport and auto-load more if needed
  useEffect(() => {
    const checkViewportFill = async () => {
      const container = document.querySelector('.members-container');
      if (container && hasMore && !loading && !loadingMore) {
        const { scrollHeight, clientHeight } = container;

        // If content doesn't fill the viewport (no scrollbar), load more
        if (scrollHeight <= clientHeight && members.length > 0) {
          console.log('Members: Content does not fill viewport, loading more automatically');
          await loadMoreMembers();
        }
      }
    };

    // Check after members are loaded and DOM is updated
    if (members.length > 0 && !loading && !loadingMore) {
      setTimeout(checkViewportFill, 100);
    }
  }, [members, loading, loadingMore, hasMore]);

  // Handle window resize to recheck viewport fill
  useEffect(() => {
    const handleResize = async () => {
      const container = document.querySelector('.members-container');
      if (container && hasMore && !loading && !loadingMore && members.length > 0) {
        const { scrollHeight, clientHeight } = container;
        if (scrollHeight <= clientHeight) {
          console.log('Members: Window resized, content no longer fills viewport, loading more');
          await loadMoreMembers();
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [members, hasMore, loading, loadingMore]);

  const fetchMembers = async (reset = false, pageOverride = null) => {
    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(1);
        setHasMore(true);
      } else {
        setLoadingMore(true);
      }

      const pageToFetch = reset ? 1 : (pageOverride || currentPage);
      console.log(`Members: Fetching page ${pageToFetch}, reset: ${reset}, override: ${pageOverride}`);

      let url = `/api/v1/users?page=${pageToFetch}&per_page=${membersPerPage}`;

      if (user?.area_id) {
        url += `&area_id=${user.area_id}`;
      }

      if (searchTerm.trim()) {
        url = `/api/v1/users/search?q=${encodeURIComponent(searchTerm)}&page=${pageToFetch}&per_page=${membersPerPage}`;
        if (user?.area_id) {
          url += `&area_id=${user.area_id}`;
        }
      }

      const response = await fetch(url, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const newMembers = data.users || [];

        console.log(`Members: Received ${newMembers.length} members for page ${pageToFetch}`);

        if (reset) {
          setMembers(newMembers);
        } else {
          setMembers(prev => [...prev, ...newMembers]);
        }

        if (data.meta) {
          setTotalMembers(data.meta.total_count || 0);
          // setTotalPages(Math.ceil((data.meta.total_count || 0) / membersPerPage));

          // Check if there are more pages to load
          const totalPagesCount = Math.ceil((data.meta.total_count || 0) / membersPerPage);
          setHasMore(pageToFetch < totalPagesCount);

          console.log(`Members: Page ${pageToFetch}/${totalPagesCount}, hasMore: ${pageToFetch < totalPagesCount}`);
        }

        // If we got fewer members than requested, we've reached the end
        if (newMembers.length < membersPerPage) {
          setHasMore(false);
          console.log('Members: Reached end of results (fewer members than requested)');
        }
      } else {
        console.error('Failed to fetch members');
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setHasMore(true);
  };

  const loadMoreMembers = async () => {
    if (hasMore && !loadingMore && !loading) {
      const nextPage = currentPage + 1;
      console.log(`Members: Loading page ${nextPage}`);

      await fetchMembers(false, nextPage);
      setCurrentPage(nextPage);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Load more when user scrolls to within 100px of the bottom
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      loadMoreMembers();
    }
  };

  const handleStartConversation = async (member) => {
    try {
      console.log('Starting conversation with:', member.username);

      // First, try to find an existing conversation between just the current user and target user
      const existingConversation = await findExistingConversation(member.username);

      if (existingConversation) {
        console.log('Found existing conversation:', existingConversation._id);
        // Open the existing conversation
        openConversation(existingConversation);
      } else {
        console.log('Creating new conversation with:', member.username);
        // Create a new conversation
        const newConversation = await createNewConversation(member.username);
        if (newConversation) {
          openConversation(newConversation);
        }
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  const findExistingConversation = async (targetUsername) => {
    try {
      // Fetch user's conversations
      const response = await fetch('/api/v1/conversations?include_latest_message=true&per_page=50', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const conversations = data.conversations || [];

        // Find a conversation that has exactly 2 participants: current user and target user
        const existingConv = conversations.find(conv => {
          const participants = conv.conversation_users || [];

          // Check if it's a 2-person conversation
          if (participants.length !== 2) return false;

          // Check if both users are in the conversation
          const usernames = participants.map(cu => cu.user?.username).filter(Boolean);
          return usernames.includes(user.username) && usernames.includes(targetUsername);
        });

        return existingConv;
      }
    } catch (error) {
      console.error('Error finding existing conversation:', error);
    }
    return null;
  };

  const createNewConversation = async (targetUsername) => {
    try {
      const formData = new FormData();
      formData.append('conversation[neighbors][]', targetUsername);
      const response = await fetch('/api/v1/conversations', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        return data.conversations || data.conversation;
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
    return null;
  };

  const openConversation = (conversation) => {
    // Close the members view and open messaging with the specific conversation
    // We'll need to pass this conversation to the parent Layout component
    console.log('Opening conversation:', conversation);
    // For now, we'll close the members view and the parent should handle opening messaging
    // In a future enhancement, we could pass the conversation ID to open directly
    onClose();
    // Trigger messaging app to open with this conversation
    // This would need to be implemented in the Layout component
    if (window.openMessagingWithConversation) {
      window.openMessagingWithConversation(conversation);
    }
  };

  const getRoleColor = (role) => {
    if (!role) return 'default';
    switch (role.name) {
      case 'Fundador': return 'secondary';
      case 'Moderador': return 'primary';
      default: return 'default';
    }
  };

  const formatJoinDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long'
    });
  };

  const getAvatarPlaceholderColor = (name) => {
    // Generate consistent colors based on name
    const colors = [
      'from-blue-100 to-blue-200 text-blue-600',
      'from-green-100 to-green-200 text-green-600',
      'from-purple-100 to-purple-200 text-purple-600',
      'from-pink-100 to-pink-200 text-pink-600',
      'from-indigo-100 to-indigo-200 text-indigo-600',
      'from-yellow-100 to-yellow-200 text-yellow-600',
      'from-red-100 to-red-200 text-red-600',
      'from-teal-100 to-teal-200 text-teal-600',
    ];

    // Simple hash function to get consistent color for same name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="fixed top-20 left-0 right-0 bottom-0 bg-gray-50 z-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm p-6 flex items-center">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-primary-100 rounded-nextui">
            <Users className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Membros da Vizinhança</h1>
            <p className="text-sm text-gray-600 mt-1">
              {user?.area?.name} • {totalMembers} {totalMembers === 1 ? 'membro' : 'membros'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 h-full overflow-y-auto members-container bg-gray-50" onScroll={handleScroll}>
        {/* Search */}
        <div className="max-w-6xl mx-auto mb-8">
          <Card className="shadow-sm border border-gray-200 rounded-nextui">
            <CardBody className="p-4">
              <Input
                placeholder="Buscar membros por nome de usuário..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                startContent={<Search className="w-5 h-5 text-gray-400" />}
                size="lg"
                variant="bordered"
                className="max-w-md"
                classNames={{
                  input: "text-base",
                  inputWrapper: "border-gray-200 hover:border-gray-300 focus-within:border-primary rounded-nextui-sm"
                }}
              />
            </CardBody>
          </Card>
        </div>

        {/* Members Grid */}
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="text-center">
                <Spinner size="lg" color="primary" />
                <p className="text-gray-600 mt-4">Carregando membros...</p>
              </div>
            </div>
          ) : members.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {members.map((member) => (
                  <Card
                    key={member._id}
                    className="shadow-post hover:shadow-lg transition-all duration-200 border border-gray-200 rounded-nextui bg-white"
                  >
                    <CardBody className="p-6">
                      <div className="flex items-start space-x-4">
                        {(member.avatar || member.image_url) ? (
                          <Avatar
                            src={member.avatar || member.image_url}
                            name={member.first_name && member.last_name
                              ? `${member.first_name} ${member.last_name}`
                              : member.username
                            }
                            size="lg"
                            className="flex-shrink-0"
                            classNames={{
                              base: "w-16 h-16",
                              img: "w-16 h-16 object-cover"
                            }}
                            showFallback
                            fallback={
                              <div className={`w-16 h-16 bg-gradient-to-br ${getAvatarPlaceholderColor(member.username)} rounded-full flex items-center justify-center`}>
                                <span className="font-semibold text-lg">
                                  {(member.first_name?.[0] || member.username?.[0] || '?').toUpperCase()}
                                </span>
                              </div>
                            }
                          />
                        ) : (
                          <div className={`w-16 h-16 bg-gradient-to-br ${getAvatarPlaceholderColor(member.username)} rounded-full flex items-center justify-center flex-shrink-0`}>
                            <span className="font-semibold text-lg">
                              {(member.first_name?.[0] || member.username?.[0] || '?').toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-base truncate text-gray-900">
                              {member.first_name && member.last_name
                                ? `${member.first_name} ${member.last_name}`
                                : member.username
                              }
                            </h3>
                            {member.role && (
                              <Chip
                                size="sm"
                                color={getRoleColor(member.role)}
                                variant="flat"
                                className="text-xs"
                              >
                                {member.role.name}
                              </Chip>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            @{member.username}
                          </p>
                          {member.summary && (
                            <p className="text-sm text-gray-700 line-clamp-3 mb-4 leading-relaxed">
                              {member.summary}
                            </p>
                          )}
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                            <span className="flex items-center space-x-1">
                              <Users className="w-3 h-3" />
                              <span>Desde {formatJoinDate(member.created_at)}</span>
                            </span>
                            {member.points && (
                              <span className="font-medium">
                                {member.points} pts
                              </span>
                            )}
                          </div>
                          {member._id !== user._id && (
                            <Button
                              size="sm"
                              color="primary"
                              variant="solid"
                              className="w-full rounded-nextui-sm font-medium"
                              startContent={<MessageCircle className="w-4 h-4" />}
                              onPress={() => handleStartConversation(member)}
                            >
                              Enviar Mensagem
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>

              {/* Loading More Indicator */}
              {loadingMore && (
                <Card className="shadow-sm border border-gray-200 rounded-nextui mb-6">
                  <CardBody className="p-6">
                    <div className="flex justify-center items-center space-x-3">
                      <Spinner size="md" color="primary" />
                      <span className="text-sm text-gray-600">Carregando mais membros...</span>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* End of Results Indicator */}
              {!hasMore && members.length > 0 && (
                <Card className="shadow-sm border border-gray-200 rounded-nextui">
                  <CardBody className="p-6">
                    <div className="text-center">
                      <div className="p-3 bg-green-100 rounded-nextui inline-block mb-3">
                        <Users className="w-6 h-6 text-green-600" />
                      </div>
                      <p className="text-sm text-gray-600">
                        Todos os membros foram carregados • {totalMembers} {totalMembers === 1 ? 'membro' : 'membros'} total
                      </p>
                    </div>
                  </CardBody>
                </Card>
              )}
            </>
          ) : (
            <Card className="shadow-sm border border-gray-200 rounded-nextui">
              <CardBody className="p-12">
                <div className="text-center">
                  <div className="p-4 bg-gray-100 rounded-nextui inline-block mb-6">
                    <Users className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-3">
                    {searchTerm ? 'Nenhum membro encontrado' : 'Nenhum membro na área'}
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    {searchTerm
                      ? 'Tente buscar com termos diferentes ou verifique a ortografia'
                      : 'Seja o primeiro a se juntar à vizinhança e comece a construir uma comunidade!'
                    }
                  </p>
                  {searchTerm && (
                    <Button
                      variant="light"
                      color="primary"
                      className="mt-4"
                      onPress={() => setSearchTerm('')}
                    >
                      Limpar busca
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Members;
